import {Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useContext, useRef} from 'react';

import {isNil} from 'ramda';

import {useLatest, useOnMount} from 'shared/web';

import {Section} from '../../components/Section/Section';
import {FinancialProductCard} from './FinancialProductCard';
import {FinancialProductControls} from './FinancialProductControls';
import {subscribeToOpenFinancialProduct} from './financialProductNavigation';
import {FinancialProductNotices} from './FinancialProductNotices';
import {FinancialProductOfferDetail} from './FinancialProductOfferDetail';
import {
  FinancialProductPreferenceContext,
  type FinancialProductPreferenceContextValue,
} from './FinancialProductPreferenceContext';
import {FinancialProductPreferenceProvider} from './FinancialProductPreferenceProvider';
import {FinancialProductProviderFacts} from './FinancialProductProviderFacts';
import {FinancialProductResultPanel} from './FinancialProductResultPanel';
import {FinancialProductSignedSnapshotNotice} from './FinancialProductSignedSnapshotNotice';
import {FinancialProductTabs} from './FinancialProductTabs';
import {hasChoosableConfiguration} from './preference/hasChoosableConfiguration';

/**
 * How the vehicle can be paid for, as the customer holding the offer link sees
 * and shapes it.
 *
 * Every control change is quoted at once from the rates the offer publishes, and
 * a dragged slider is quoted while it moves. Releasing a control stores that
 * choice; the stored answer is the quote of record and replaces the local one,
 * so the customer never confirms a quote the frontend assembled, and the seller
 * sees the same choice on the business case. Without published rates (an older
 * backend, or a product not offered today) the panel keeps its rows with
 * placeholders until the stored answer arrives. A product nobody configured is
 * absent from the payload and gets no tab; one the vehicle no longer offers
 * anything on is present but unavailable, and keeps a tab only while it is the
 * customer's stored choice or the tab they are on. One that is unavailable only
 * on the stored choice keeps its tab and opens on a choice that can be bought.
 * A vehicle with nothing configured at all renders no section.
 *
 * Nothing here decides for itself what an offer now says: a refused write and a
 * signed contract both answer with the authoritative state, and the surface
 * renders that rather than the choice it was refused.
 */
export function FinancialProducts() {
  const preference = useContext(FinancialProductPreferenceContext);

  // Rendered on its own (outside the page layout), the section owns the customer's choice itself.
  if (isNil(preference)) {
    return (
      <FinancialProductPreferenceProvider>
        <FinancialProducts />
      </FinancialProductPreferenceProvider>
    );
  }

  return <FinancialProductsSection preference={preference} />;
}

interface FinancialProductsSectionProps {
  preference: FinancialProductPreferenceContextValue;
}

function FinancialProductsSection(props: FinancialProductsSectionProps) {
  const {
    products,
    offers,
    refusal,
    rebased,
    isReadOnly,
    opened,
    displayedCoordinates,
    quoteLifecycle,
    commit,
    preview,
    openProduct,
    getDisplayed,
  } = props.preference;

  const latestOpenProduct = useLatest(openProduct);
  const latestOffers = useLatest(offers);
  const unsubscribe = useRef<VoidFunction | null>(null);

  useOnMount(
    () => {
      unsubscribe.current = subscribeToOpenFinancialProduct((productType) => {
        if (latestOffers.current.some((offer) => offer.productType === productType)) {
          latestOpenProduct.current(productType);
        }
      });
    },
    () => unsubscribe.current?.()
  );

  if (isNil(opened) || isNil(displayedCoordinates)) {
    return null;
  }

  const displayed = getDisplayed(opened.offer.productType);

  return (
    <Section id="FINANCIAL_PRODUCTS" data-testid="financialProducts">
      <VStack spacing={6}>
        <Show when={offers.length > 1}>
          <FinancialProductTabs
            offers={offers}
            value={opened.offer.productType}
            onChange={openProduct}
            data-testid="financialProducts-product"
          />
        </Show>
        <FinancialProductNotices
          offer={opened.offer}
          displayed={displayed}
          refusal={refusal}
          rebased={rebased}
          quoteLifecycle={quoteLifecycle}
          data-testid="financialProducts"
        />
        <FinancialProductCard
          controls={match(!isReadOnly && hasChoosableConfiguration(opened.offer))
            .with(true, () => (
              <FinancialProductControls
                offer={opened.offer}
                coordinates={opened.coordinates}
                vehiclePriceWithVat={products?.vehiclePriceWithVat}
                onChange={commit}
                onPreview={preview}
                data-testid="financialProducts"
              />
            ))
            .otherwise(() => null)}
          result={
            <FinancialProductResultPanel>
              <FinancialProductOfferDetail
                productType={opened.offer.productType}
                coordinates={displayedCoordinates}
                displayed={displayed}
                isQuoteFailed={displayed.source === 'NONE' && quoteLifecycle === 'failed'}
                data-testid="financialProducts"
              />
            </FinancialProductResultPanel>
          }
          footer={<FinancialProductProviderFacts offer={opened.offer} />}
        />
        <Show when={products?.mode === 'CONTRACT_SNAPSHOT'}>
          <FinancialProductSignedSnapshotNotice data-testid="financialProducts" />
        </Show>
      </VStack>
    </Section>
  );
}
